import { useState } from "react";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Plus } from "lucide-react";
import { AttributeManager } from "./albums/AttributeManager";
import { AlbumLineForm } from "./albums/AlbumLineForm";
import { AlbumLineListItem } from "./albums/AlbumLineListItem";
import { useAlbumAdmin } from "../../hooks/useAlbumAdmin";

const ATTRIBUTE_PANELS = [
  { kind: "type", title: "Tipos de álbum" },
  { kind: "size", title: "Tamaños" },
  { kind: "pages", title: "Cantidades de páginas" },
  { kind: "cover", title: "Portadas" },
  { kind: "color", title: "Colores" },
  { kind: "paper", title: "Papeles" },
  { kind: "laminate", title: "Laminados" },
  { kind: "personalization", title: "Personalizaciones" },
];

export const AdminAlbums = () => {
  const { attributes, lines, loading, createAttribute, updateAttribute, moveAttribute, createLine, updateLine, toggleLineField, bulkUpdatePrices, moveLine } = useAlbumAdmin();
  const [view, setView] = useState("lines");
  const [editingLine, setEditingLine] = useState(null);
  const [creating, setCreating] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkPrices, setBulkPrices] = useState({});

  const attrById = Object.values(attributes).flat().reduce((acc, a) => ({ ...acc, [a.id]: a }), {});

  const startBulk = () => {
    setBulkPrices(Object.fromEntries(lines.map((l) => [l.id, l.price])));
    setBulkMode(true);
  };

  const saveBulk = async () => {
    await bulkUpdatePrices(Object.entries(bulkPrices).map(([id, price]) => ({ id, price })));
    setBulkMode(false);
  };

  if (loading) return <p className="text-muted-foreground" data-testid="admin-albums-loading">Cargando catálogo de álbumes...</p>;

  return (
    <div className="space-y-6" data-testid="admin-albums">
      <Tabs value={view} onValueChange={setView}>
        <TabsList data-testid="admin-albums-view-tabs">
          <TabsTrigger value="lines" data-testid="admin-albums-tab-lines">Líneas de álbum</TabsTrigger>
          <TabsTrigger value="catalog" data-testid="admin-albums-tab-catalog">Catálogo base</TabsTrigger>
        </TabsList>
      </Tabs>

      {view === "catalog" ? (
        <div className="grid md:grid-cols-2 gap-4">
          {ATTRIBUTE_PANELS.map((p) => (
            <AttributeManager key={p.kind} kind={p.kind} title={p.title} items={attributes[p.kind] || []} onCreate={createAttribute} onUpdate={updateAttribute} onMove={moveAttribute} />
          ))}
        </div>
      ) : (
        <Card className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Líneas de álbum ({lines.length})</h3>
            <div className="flex gap-2">
              {bulkMode ? (
                <>
                  <Button size="sm" variant="outline" onClick={() => setBulkMode(false)} data-testid="admin-albums-bulk-cancel">Cancelar</Button>
                  <Button size="sm" onClick={saveBulk} data-testid="admin-albums-bulk-save">Guardar todos los precios</Button>
                </>
              ) : (
                <>
                  <Button size="sm" variant="outline" onClick={startBulk} data-testid="admin-albums-bulk-start">Modificar precios en lote</Button>
                  <Button size="sm" onClick={() => setCreating(true)} data-testid="admin-albums-new-line"><Plus size={14} className="mr-1" /> Nueva línea</Button>
                </>
              )}
            </div>
          </div>
          <div className="space-y-2">
            {lines.map((line, idx) => (
              <AlbumLineListItem
                key={line.id} line={line} attrById={attrById} idx={idx} total={lines.length}
                onEdit={setEditingLine} onToggle={toggleLineField} onMove={moveLine}
                bulkMode={bulkMode} bulkPrice={bulkPrices[line.id] ?? line.price}
                onBulkPriceChange={(id, v) => setBulkPrices((b) => ({ ...b, [id]: v }))}
              />
            ))}
          </div>
        </Card>
      )}

      <Dialog open={!!editingLine} onOpenChange={(v) => !v && setEditingLine(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Editar línea de álbum</DialogTitle></DialogHeader>
          {editingLine && (
            <AlbumLineForm attributes={attributes} initial={editingLine} onSubmit={(p) => updateLine(editingLine.id, p)} onCancel={() => setEditingLine(null)} />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nueva línea de álbum</DialogTitle></DialogHeader>
          <AlbumLineForm attributes={attributes} onSubmit={createLine} onCancel={() => setCreating(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
};
